import type React from "react";
import { failureCategoryForStatus, scrubErrorText } from "@fastn/core";
import { capture, registerOnboardingContext } from "@/lib/posthog";
import { isNetworkFailure, NETWORK_FAILURE_MESSAGE } from "@/lib/error-analytics";
import { recentFailedRequest } from "@/lib/request-failures";

/**
 * Every button and link pressed inside onboarding, named by its data-testid.
 * One capture-phase listener on the screen's root sees each click before the
 * element handles it, so a new button is tracked the moment it ships, with no
 * wiring of its own. Only the test id is sent: never text, values or URLs.
 */
/** The last onboarding button pressed and the last step failure this visit:
 *  what onboarding_abandoned reports as what they did before leaving. */
let lastAction: { screen: string; action: string; at: number } | null = null;
let lastFailureReason: string | null = null;
/** The last few actions, oldest first: a Skip's own clicks end the list, so
 *  what came before them is still there to read. */
const RECENT_MAX = 5;
let recentActions: string[] = [];

export function lastOnboardingActivity(now: number = Date.now()): {
  last_action: string | null;
  last_action_screen: string | null;
  seconds_since_last_action: number | null;
  last_failure_reason: string | null;
  recent_actions: string[];
} {
  return {
    last_action: lastAction?.action ?? null,
    last_action_screen: lastAction?.screen ?? null,
    seconds_since_last_action: lastAction ? Math.round((now - lastAction.at) / 1000) : null,
    last_failure_reason: lastFailureReason,
    recent_actions: [...recentActions],
  };
}

export function trackOnboardingClicks(screen: string): (e: React.MouseEvent) => void {
  return (e) => {
    const el = (e.target as Element | null)?.closest?.("button, a, summary, input, select, [role='button'], [role='option'], [data-testid]");
    const id = el?.closest("[data-testid]")?.getAttribute("data-testid");
    if (!el || !id) return;
    const tag = el.tagName.toLowerCase();
    const kind = tag === "a" ? "link" : tag === "input" || tag === "select" ? "input" : tag === "button" || tag === "summary" || el.getAttribute("role") === "button" || el.getAttribute("role") === "option" ? "button" : "other";
    if (kind === "other") return;
    lastAction = { screen, action: id, at: Date.now() };
    recentActions = [...recentActions, `${screen}:${id}`].slice(-RECENT_MAX);
    capture("onboarding_action", { screen, action: id, kind });
  };
}

const stepFailures = new Map<string, number>();

/**
 * A step of onboarding failed. `reason` is a fixed code per failure site. The
 * error's message is sent scrubbed (scrubErrorText): it is what the person was
 * shown, and the typed values and ids it can echo are replaced.
 */
export function reportStepFailed(screen: string, step: number, reason: string, err?: unknown): void {
  const raw = (err as { status?: unknown } | null | undefined)?.status;
  const status = typeof raw === "number" && raw > 0 ? raw : null;
  const message = (err as { message?: unknown } | null | undefined)?.message;
  const network = status === null && isNetworkFailure(message);
  // A hint only: the request that failed last may not be this step's.
  const failed = recentFailedRequest();
  const key = `${screen}:${reason}`;
  const attempt = (stepFailures.get(key) ?? 0) + 1;
  stepFailures.set(key, attempt);
  lastFailureReason = reason;
  capture("onboarding_step_failed", {
    screen,
    step,
    reason,
    failure_category: network ? "network" : failureCategoryForStatus(status),
    http_status: status,
    attempt_number: attempt,
    error_message: network ? NETWORK_FAILURE_MESSAGE : scrubErrorText(message),
    failed_request: failed?.request ?? null,
    failed_request_status: failed?.status ?? null,
  });
}

/**
 * Stamp every event sent while an onboarding screen is open with that screen,
 * so error_shown and api_request_failed can be broken down by the step the
 * person was on. Cleared with null when onboarding unmounts.
 */
export function setOnboardingScreen(screen: string | null, step: number | null): void {
  registerOnboardingContext(screen === null ? null : { onboarding_screen: screen, onboarding_step: step ?? 0 });
}

/** The step that failed has now succeeded: the next failure counts from 1 again. */
export function clearStepFailures(screen: string, reason: string): void {
  stepFailures.delete(`${screen}:${reason}`);
  if (lastFailureReason === reason) lastFailureReason = null;
}

/** Test seam. */
export function __resetOnboardingActivityForTests(): void {
  lastAction = null;
  lastFailureReason = null;
  recentActions = [];
  stepFailures.clear();
}
